import type { MouseEvent, MouseEventHandler, Touch, TouchEventHandler } from 'react';
import { useEffect, useState } from 'react';
import clsx from 'clsx';
import type { WordPosition } from '@/types';
import BorderImage from '@/assets/images/3-circles-dashes.svg';
import Logo from '@/assets/images/3-scratch-the-code.png';
import { BUTTON_APPEAR_DELAY, REVEAL_RADIUS, WORDS } from './config';
import { WordInput } from './WordInput';
import { useAnimate } from 'motion/react';

const generateRandomPositions = (): WordPosition[] =>
  WORDS.filter(word => word.hiddenInBackground)
    .sort(() => Math.random() - 0.5)
    .map((word, index, array) => {
      const segmentSize = 80 / array.length;
      const minY = index * segmentSize;
      const maxY = minY + segmentSize;
      return {
        word,
        x: Math.random() * 85, // 0-85% of width
        y: Math.random() * (maxY - minY) + minY, // Segment based on index
      };
    });

interface Props {
  onComplete: () => void;
  onExitStart: () => void;
}

export const Step3 = ({ onComplete, onExitStart }: Props) => {
  const [lockedWords, setLockedWords] = useState<boolean[]>(Array(WORDS.length).fill(false));
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [showMask, setShowMask] = useState(false);
  const [hiddenWordPositions] = useState<WordPosition[]>(generateRandomPositions);
  const [showButton, setShowButton] = useState(false);
  const [showInputs, setShowInputs] = useState(false);
  const [pageTransitionScope, animate] = useAnimate<HTMLDivElement>();

  useEffect(() => {
    const enterAnimation = async () => {
      // initial state, instant
      await animate(pageTransitionScope.current, { opacity: 0, background: 'var(--color-red)' }, { duration: 0 });

      // actual animation
      await animate(pageTransitionScope.current, { opacity: 1 }, { duration: 1, ease: 'easeInOut' });
      await animate(pageTransitionScope.current, { background: '' }, { duration: 0, ease: 'easeInOut' });
    };

    void enterAnimation();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const exitAnimation = async () => {
    await animate(pageTransitionScope.current, { opacity: 0 }, { duration: 2, ease: 'easeInOut' });
    await animate(pageTransitionScope.current.parentElement, { scaleY: 2 }, { duration: 2, ease: 'easeInOut' });
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';

    return () => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    };
  }, []);

  const handleWordCorrect = (wordIndex: number) => {
    const newLockedWords = [...lockedWords];
    newLockedWords[wordIndex] = true;
    setLockedWords(newLockedWords);

    // Check if all words are correct
    if (newLockedWords.every(locked => locked)) {
      handleStepSuccess();
    }
  };

  const handleMove = (event: Touch | MouseEvent, rect: DOMRect) => {
    setMousePos({
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    });
    setShowMask(true);

    if (!showButton) {
      setTimeout(() => setShowButton(true), BUTTON_APPEAR_DELAY);
    }
  };

  const handleMouseMove: MouseEventHandler<HTMLDivElement> = event => handleMove(event, event.currentTarget.getBoundingClientRect());

  const handleTouchMove: TouchEventHandler<HTMLDivElement> = event => handleMove(event.touches[0], event.currentTarget.getBoundingClientRect());

  const handleMouseLeave = () => setShowMask(false);

  const handleClickToShowInputs = () =>
    setShowInputs(prevState => {
      const newState = !prevState;

      window.scrollTo({ top: 0, behavior: 'instant' });
      document.documentElement.style.overflow = newState ? '' : 'hidden';
      document.body.style.overflow = newState ? '' : 'hidden';

      return newState;
    });

  const handleStepSuccess = () => {
    onExitStart();
    void exitAnimation().then(onComplete);
  };

  return (
    <div className="min-h-dvh w-screen bg-red flex flex-col relative select-none">
      <div className="flex w-full">
        <div className="flex-1 p-3 bg-beige flex items-center justify-center text-center text-dark-beige/50">Find the&nbsp;Password</div>
        <div className="flex-1 p-3 bg-green flex items-center justify-center text-center text-light-green/50">Target your&nbsp;Mate</div>
        <div className="flex-1 p-3 bg-red flex items-center justify-center text-center text-dark-red font-bold">Scratch the&nbsp;Code</div>
      </div>

      <div
        ref={pageTransitionScope}
        className={clsx(
          'flex-1 flex flex-col items-center justify-start gap-8 py-8 px-4 relative overflow-hidden border-image-dashed border-red text-dark-red',
          showInputs ? 'bg-red' : 'bg-white',
        )}
        style={{
          borderImageSource: `url(${BorderImage})`,
        }}
        onMouseMove={showInputs ? undefined : handleMouseMove}
        onMouseLeave={showInputs ? undefined : handleMouseLeave}
        onTouchMove={showInputs ? undefined : handleTouchMove}
        onTouchEnd={showInputs ? undefined : handleMouseLeave}
      >
        <img src={Logo} alt="Scratch the Code" className="h-24 md:h-32 relative z-10" />

        <button
          onClick={showButton ? handleClickToShowInputs : undefined}
          className={clsx('button text-sm md:text-base bg-red transition-all duration-300 z-10', showButton ? 'opacity-100' : 'opacity-0')}
        >
          {showInputs ? 'Retourner à la recherche des mots' : 'Saisir les mots trouvés'}
        </button>

        {!showInputs && (
          <>
            <div className="relative inset-0 pointer-events-none w-4/5 flex-1 grid grid-cols-6">
              {hiddenWordPositions.map(wordPosition => (
                <div
                  key={wordPosition.word.id}
                  className="absolute text-sm md:text-3xl font-bold select-none whitespace-nowrap -translate-1/2"
                  style={{
                    left: `${wordPosition.x}%`,
                    top: `${wordPosition.y}%`,
                  }}
                >
                  {wordPosition.word.id}. {wordPosition.word.word}
                </div>
              ))}
            </div>

            {/* desktop */}
            <div
              // negative inset is due to border-image width
              className="absolute pointer-events-none bg-red hidden md:block -inset-12"
              style={{
                maskImage: showMask
                  ? `radial-gradient(circle ${REVEAL_RADIUS * 1.25}px at ${mousePos.x}px ${mousePos.y}px, transparent 0, transparent 80%, black 100%)`
                  : undefined,
              }}
            />

            {/* mobile */}
            <div
              // negative inset is due to border-image width
              className="absolute pointer-events-none bg-red md:hidden -inset-8"
              style={{
                maskImage: showMask
                  ? `radial-gradient(circle ${REVEAL_RADIUS}px at ${mousePos.x}px ${mousePos.y - 65}px, transparent 0, transparent 80%, black 100%)`
                  : undefined,
              }}
            />
          </>
        )}

        <div
          className={clsx(
            'justify-center items-center bg-red inset-0 w-full md:w-auto transition-opacity duration-500 ease-in-out',
            showInputs ? 'flex opacity-100' : 'hidden opacity-0',
          )}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 relative z-10 w-full">
            {WORDS.map((wordData, index) => (
              <WordInput key={wordData.id} wordData={wordData} locked={lockedWords[index]} onCorrect={() => handleWordCorrect(index)} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
